import { useContext, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { UserContext } from "../../contexts/UserContext";

function formatDateLabel(value) {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function safeText(value, fallback = "—") {
  return String(value || "").trim() || fallback;
}

function jobTypeLabel(stop = {}) {
  return (
    stop.jobTypeName ||
    stop.jobType ||
    stop.jobTypeLabel ||
    stop.cardType ||
    stop.type ||
    "Job"
  );
}

function statusTone(status = "") {
  const s = String(status).toLowerCase();
  if (["finished", "completed", "complete"].includes(s)) {
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  }
  if (["arrived", "onsite"].includes(s)) {
    return "bg-sky-100 text-sky-800 border-sky-200";
  }
  if (["enroute", "on the way", "on_the_way"].includes(s)) {
    return "bg-amber-100 text-amber-800 border-amber-200";
  }
  if (["skipped"].includes(s)) {
    return "bg-rose-100 text-rose-800 border-rose-200";
  }
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function getStopStatus(stop = {}) {
  return (
    stop.status ||
    stop.jobStatus ||
    stop.state ||
    (stop.finishedAt ? "Finished" : "Assigned")
  );
}

function getStopIdentity(stop = {}, runKey = "") {
  return (
    stop.jobId ||
    stop.id ||
    stop.jobRefId ||
    [
      runKey,
      stop.siteId || stop.siteName || stop.siteLabel || stop.customerSite || "",
      stop.address || stop.siteAddress || "",
      stop.jobTypeCode || stop.jobType || stop.jobTypeName || "",
      stop.poNumber || stop.po || "",
    ].join("|")
  );
}

function dedupeStops(stops = [], runKey = "") {
  const map = new Map();

  (Array.isArray(stops) ? stops : []).filter(Boolean).forEach((stop) => {
    const key = getStopIdentity(stop, runKey);
    if (!map.has(key)) {
      map.set(key, stop);
    } else {
      map.set(key, { ...map.get(key), ...stop });
    }
  });

  return Array.from(map.values());
}

function DriverPreviewCard({ stop, index }) {
  const status = getStopStatus(stop);
  const photos = Array.isArray(stop.photos)
    ? stop.photos
    : Array.isArray(stop.uploadedPhotos)
      ? stop.uploadedPhotos
      : [];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-700">
              Stop {index + 1}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone(status)}`}
            >
              {safeText(status, "Assigned")}
            </span>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {safeText(
                stop.siteName ||
                  stop.siteLabel ||
                  stop.customerSite ||
                  stop.customerName ||
                  stop.companyName,
                "Unnamed site",
              )}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {safeText(
                stop.address || stop.siteAddress || stop.location,
                "No address",
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:min-w-[220px]">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
              Job
            </div>
            <div className="mt-1 text-sm font-medium text-gray-800">
              {jobTypeLabel(stop)}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
              Bins
            </div>
            <div className="mt-1 text-sm font-medium text-gray-800">
              {safeText(stop.binCount || stop.qty || stop.quantity, "—")}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            Customer
          </div>
          <div className="mt-1 text-sm text-gray-800">
            {safeText(stop.customerName || stop.companyName)}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            Vehicle
          </div>
          <div className="mt-1 text-sm text-gray-800">
            {safeText(stop.vehicleLabel || stop.vehicleRego || stop.rego)}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            PO / Tracker
          </div>
          <div className="mt-1 text-sm text-gray-800">
            {safeText(stop.poNumber || stop.po || stop.epaWasteTracker)}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            Photos
          </div>
          <div className="mt-1 text-sm text-gray-800">{photos.length}</div>
        </div>
      </div>

      {(stop.notes ||
        stop.siteAccessNotes ||
        stop.driverNote ||
        stop.comment) && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
            Visible note
          </div>
          <div className="mt-1 text-sm text-amber-900">
            {stop.notes ||
              stop.siteAccessNotes ||
              stop.driverNote ||
              stop.comment}
          </div>
        </div>
      )}
    </div>
  );
}

function RunSection({ runKey, stops = [], runMeta = {} }) {
  const sortedStops = dedupeStops(stops);
  const companyName =
    runMeta?.companyName || sortedStops[0]?.companyName || "Company";
  const vehicle =
    runMeta?.vehicleLabel ||
    runMeta?.vehicleRego ||
    sortedStops[0]?.vehicleLabel ||
    sortedStops[0]?.vehicleRego ||
    "—";

  return (
    <section className="rounded-[24px] border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
              {runKey}
            </div>
            <h2 className="mt-2 text-lg font-semibold text-gray-900">
              {companyName}
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                Stops
              </div>
              <div className="mt-1 text-base font-semibold text-gray-900">
                {sortedStops.length}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                Vehicle
              </div>
              <div className="mt-1 text-sm font-medium text-gray-800">
                {vehicle}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 col-span-2 sm:col-span-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                Layout
              </div>
              <div className="mt-1 text-sm font-medium capitalize text-gray-800">
                {safeText(runMeta?.uiVariant, "default")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 p-4 sm:p-5">
        {sortedStops.length ? (
          sortedStops.map((stop, idx) => (
            <DriverPreviewCard
              key={stop.jobId || stop.id || `${runKey}-${idx}`}
              stop={stop}
              index={idx}
            />
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            No jobs in this run.
          </div>
        )}
      </div>
    </section>
  );
}

export default function AdminDriverView() {
  const { userInfo } = useContext(UserContext);
  const [date, setDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" }),
  );
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const role = String(userInfo?.role || "").toLowerCase();
  const isAdmin = role === "admin";

  useEffect(() => {
    let active = true;
    setLoadingDrivers(true);

    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "users"), where("role", "==", "driver")),
        );
        if (!active) return;

        const nextDrivers = snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .sort((a, b) => {
            const an = String(a.name || a.displayName || a.email || "");
            const bn = String(b.name || b.displayName || b.email || "");
            return an.localeCompare(bn);
          });

        setDrivers(nextDrivers);
        setSelectedDriverId((current) => current || nextDrivers[0]?.id || "");
      } catch (error) {
        console.error("Failed to load drivers:", error);
      } finally {
        if (active) setLoadingDrivers(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedDriverId || !date) {
      setAssignments([]);
      return undefined;
    }

    setLoadingAssignments(true);

    const q = query(
      collection(db, "assignments"),
      where("driverId", "==", selectedDriverId),
      where("date", "==", date),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAssignments(next);
        setLoadingAssignments(false);
      },
      (error) => {
        console.error("Failed to load driver assignments:", error);
        setAssignments([]);
        setLoadingAssignments(false);
      },
    );

    return () => unsub();
  }, [selectedDriverId, date]);

  const mergedRuns = useMemo(() => {
    const runMap = {};

    assignments.forEach((assignment) => {
      const runs = assignment?.runs || {};
      const runMeta = assignment?.runMeta || {};

      Object.entries(runs).forEach(([runKey, stops]) => {
        if (!runMap[runKey]) {
          runMap[runKey] = {
            runKey,
            runMeta: runMeta?.[runKey] || {},
            stops: [],
          };
        }

        if (runMeta?.[runKey]) {
          runMap[runKey].runMeta = {
            ...runMap[runKey].runMeta,
            ...runMeta[runKey],
          };
        }

        runMap[runKey].stops.push(
          ...(Array.isArray(stops) ? stops.filter(Boolean) : []),
        );
      });
    });

    return Object.values(runMap)
      .map((run) => ({
        ...run,
        stops: dedupeStops(run.stops, run.runKey),
      }))
      .sort((a, b) => {
        const na = parseInt(String(a.runKey).match(/(\d+)/)?.[1] || "999", 10);
        const nb = parseInt(String(b.runKey).match(/(\d+)/)?.[1] || "999", 10);
        return na - nb;
      });
  }, [assignments]);

  const totalStops = useMemo(
    () => mergedRuns.reduce((sum, run) => sum + (run.stops?.length || 0), 0),
    [mergedRuns],
  );

  if (!isAdmin) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 xl:px-8">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800 shadow-sm">
          This page is available to admin users only.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 sm:space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
              Driver
            </label>
            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              disabled={loadingDrivers}
              className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:bg-gray-50"
            >
              {!drivers.length && <option value="">No drivers found</option>}
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name ||
                    driver.displayName ||
                    driver.email ||
                    driver.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
            />
          </div>

          <div className="ml-auto text-xs text-gray-500">
            <span className="font-semibold text-amber-700">Read-only preview</span>
            {" · "}
            {mergedRuns.length} runs · {totalStops} jobs
          </div>
        </div>
      </div>

      {loadingAssignments ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500 shadow-sm">
          Loading driver assignments...
        </div>
      ) : !selectedDriverId ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500 shadow-sm">
          Select a driver to view their jobs.
        </div>
      ) : !mergedRuns.length ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500 shadow-sm">
          No assignments found for this driver on {formatDateLabel(date)}.
        </div>
      ) : (
        mergedRuns.map((run) => (
          <RunSection
            key={run.runKey}
            runKey={run.runKey}
            runMeta={run.runMeta}
            stops={run.stops}
          />
        ))
      )}
    </div>
  );
}
